import {
  createFileRoute,
  useNavigate,
  useRouteContext,
  useSearch,
} from "@tanstack/react-router";
import { addMonths, endOfDay, endOfMonth, isBefore, min } from "date-fns";
import { useMemo, type JSX } from "react";

import Left from "@repo/icons/default/Left";
import Settings from "@repo/icons/default/Settings";
import { CpsAlert } from "corpus";
import { CustomerContactDashboardRescuedAppointmentsSection } from "@/components/assist/rescued-appointment-overview/rescued-appointments-section";
import { Page } from "@/components/ui/layout/page/page";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";

import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
  HeaderUpButton,
} from "@/components/ui/layout/header/header";
import { DashboardTimePeriodSelector } from "@/components/assist/dashboard/dashboard-time-period-selector";
import { useInitialDateForOverviews } from "@/hooks/use-initial-date-for-overviews";
import { AssistFeatures } from "@/enums/assist-features";
import { validateFeatureAccess } from "@/lib/assist-feature-guard";
import { useFormattedMonthsArray } from "@/hooks/use-formatted-month-array-for-overview";
import { getMonthDateFromFormattedString } from "@/lib/date";
import {
  PageTitleMainContent,
  PageTitleRoot,
} from "@/components/ui/layout/page-title/page-title-root";
import { RescuedAppointmentDashboardTotalRescuedSection } from "@/components/assist/rescued-appointment-overview/rescued-appointment-dashboard-total-rescued-section";
import { RescuedAppointmentDashboardUnscheduledAppointmentsSection } from "@/components/assist/rescued-appointment-overview/rescued-appointment-dashboard-unscheduled-appointments-section";
import { RescuedAppointmentDashboardCancelledAppointmentsSection } from "@/components/assist/rescued-appointment-overview/rescued-appointment-dashboard-cancelled-appointments-section";
import { RescuedAppointmentDashboardNoShowAppointmentsSection } from "@/components/assist/rescued-appointment-overview/rescued-appointment-dashboard-no-show-appointments-section";
import { formatWithZonedDate, zonedDate } from "@/lib/helpers/date";
import { useRescuedAppointmentOverviewData } from "@/hooks/use-rescued-appointment-overview-data";

const RescuedAppointmentOverviewPage = (): JSX.Element => {
  const { user, flags } = useRouteContext({ from: "__root__" });
  const navigate = useNavigate();

  const searchParams: Record<string, string> = useSearch({
    from: "/settings/assist/rescued-appointment-overview/",
  });

  const showNewRescuedAppointmentOverview = useMemo(
    () => flags["exibe-novo-relatorio-de-recuperacao-de-agendamentos"],
    [flags],
  );

  const dataCollectionStart = new Date(2024, 10);

  const memberFirstMonth = user.dataValidacao ?? dataCollectionStart;

  const earliestDate = isBefore(memberFirstMonth, dataCollectionStart)
    ? dataCollectionStart
    : memberFirstMonth;

  const endOfToday = endOfDay(zonedDate(new Date()));

  const monthsList = useFormattedMonthsArray({
    fromDate: earliestDate,
    toDate: endOfToday,
  });

  const selectedMonth: string = searchParams.selectedMonth
    ? String(searchParams.selectedMonth)
    : monthsList[monthsList.length - 1];

  const selectedMonthDate = getMonthDateFromFormattedString(selectedMonth);

  const fromDate = useInitialDateForOverviews(selectedMonthDate);

  const fourMonthPeriodEndDate = endOfMonth(addMonths(zonedDate(new Date(fromDate)), 3));

  const toDateLimit = min([fourMonthPeriodEndDate, endOfToday]);

  const toDate = formatWithZonedDate(toDateLimit, "yyyy-MM-dd HH:mm:ss");

  const { rescuedAppointmentsData, queryResult, shouldShowAsyncWrapper } =
    useRescuedAppointmentOverviewData({
      codUsuario: user.codUsuario,
      fromDate,
      toDate,
      monthsList,
      selectedMonth,
    });

  const renderDashboardContent = (): JSX.Element => {
    if (showNewRescuedAppointmentOverview) {
      return (
        <>
          <PageTitleRoot>
            <PageTitleMainContent>
              Veja o desempenho das automações de recuperação
            </PageTitleMainContent>
          </PageTitleRoot>

          <CpsAlert
            type="info"
            title="Atualização dos dados"
            description="Os dados do relatório de recuperação podem levar até 24 horas para serem atualizados."
          />

          <RescuedAppointmentDashboardTotalRescuedSection
            data={rescuedAppointmentsData}
            selectedMonth={selectedMonth}
            onSelectedMonthChange={onSelectedMonthChange}
            fromDate={fromDate}
            toDate={toDate}
          />

          <RescuedAppointmentDashboardUnscheduledAppointmentsSection
            data={rescuedAppointmentsData}
            selectedMonth={selectedMonth}
            onSelectedMonthChange={onSelectedMonthChange}
            fromDate={fromDate}
            toDate={toDate}
          />

          <RescuedAppointmentDashboardCancelledAppointmentsSection
            data={rescuedAppointmentsData}
            selectedMonth={selectedMonth}
            onSelectedMonthChange={onSelectedMonthChange}
            fromDate={fromDate}
            toDate={toDate}
          />

          <RescuedAppointmentDashboardNoShowAppointmentsSection
            data={rescuedAppointmentsData}
            selectedMonth={selectedMonth}
            onSelectedMonthChange={onSelectedMonthChange}
            fromDate={fromDate}
            toDate={toDate}
          />
        </>
      );
    }

    return (
      <CustomerContactDashboardRescuedAppointmentsSection
        fragmentData={rescuedAppointmentsData}
        selectedMonth={selectedMonth}
      />
    );
  };

  if (!searchParams.selectedMonth) {
    searchParams.selectedMonth = selectedMonth;
    void navigate({ to: ".", search: searchParams, replace: true });
  }

  const onSelectedMonthChange = (newMonth: string): void => {
    const search = {
      ...searchParams,
    };

    search.selectedMonth = newMonth;
    void navigate({ to: ".", search, replace: true });
  };

  const onSettingsButtonClick = (): void => {
    void navigate({
      to: "/settings/assist/feature-settings",
    });
  };

  return (
    <>
      {showNewRescuedAppointmentOverview ? (
        <HeaderRoot>
          <HeaderUpButton icon={Left} align="start" />
          <HeaderTitle title="Recuperação de agendamentos" align="center" />
          <HeaderButton
            icon={Settings}
            align="end"
            onClick={onSettingsButtonClick}
            data-testid="header-settings-button"
          />
        </HeaderRoot>
      ) : (
        <HeaderRoot>
          <HeaderUpButton icon={Left} align="start" />
          <HeaderTitle title="Recuperação de agendamentos" align="center" />
        </HeaderRoot>
      )}

      <DashboardTimePeriodSelector
        selectedMonth={searchParams.selectedMonth}
        monthsList={monthsList}
        onSelectedMonthChange={onSelectedMonthChange}
      />
      <Page>
        {shouldShowAsyncWrapper ? (
          <AsyncDataWrapper {...queryResult}>{renderDashboardContent()}</AsyncDataWrapper>
        ) : (
          renderDashboardContent()
        )}
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/assist/rescued-appointment-overview/")({
  component: RescuedAppointmentOverviewPage,
  beforeLoad: ({ context, location }) => {
    const user = context.user;
    validateFeatureAccess({
      location,
      user,
      codFuncionalidade: AssistFeatures.RescueLeftScheduling,
    });
  },
});
